import React, { useCallback, useEffect, useState } from "react";
import { Check, MapPin, ChevronLeft, ChevronRight, Phone, Clock, Mail, Award, Users, CalendarDays, ShieldCheck } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { COMPANY, BRANDS } from "../lib/constants";

const ABOUT_IMG =
  "https://customer-assets.emergentagent.com/job_tire-commerce/artifacts/2v1xet0y_a4db9fb0-cfff-4bcc-acb9-9db2004f6b53.jpeg";

const SHOP_PHOTOS = [
  { src: "https://customer-assets.emergentagent.com/job_tire-commerce/artifacts/v8ysb1hp_IMG_6693.jpeg", caption: "A nossa oficina na Póvoa de Santa Iria" },
  { src: "https://customer-assets.emergentagent.com/job_tire-commerce/artifacts/fofhlf8j_IMG_6690.jpeg", caption: "Equipamento profissional de alinhamento" },
  { src: "https://customer-assets.emergentagent.com/job_tire-commerce/artifacts/xxjvplar_IMG_6688.jpeg", caption: "Trabalho de manutenção em curso" },
  { src: "https://customer-assets.emergentagent.com/job_tire-commerce/artifacts/5wvw7gwd_IMG_6689.jpeg", caption: "Alinhamento computorizado 3D" },
  { src: "https://customer-assets.emergentagent.com/job_tire-commerce/artifacts/cpfit712_IMG_6687.jpeg", caption: "Detalhe — montagem de pneus" },
  { src: "https://customer-assets.emergentagent.com/job_tire-commerce/artifacts/w6duu8uu_IMG_6699.jpeg", caption: "Trabalhamos com todas as marcas — premium incluído" },
];

const VALUES = [
  "Equipa técnica com certificação oficial",
  "Equipamento de diagnóstico 3D de última geração",
  "Apenas marcas com reputação comprovada",
  "Garantia escrita em todos os serviços",
  "Atendimento rápido — no dia, na maioria dos casos",
];

const STATS = [
  { icon: CalendarDays, value: "2003", label: "Desde", accent: true },
  { icon: Users, value: "+300", label: "Clientes satisfeitos" },
  { icon: Award, value: "Masters", label: "Hankook certificada" },
  { icon: ShieldCheck, value: "ConfortAuto", label: "Rede oficial" },
];

const CAR_BRANDS = [
  { name: "Porsche", logo: "/brands/cars/porsche.png" },
  { name: "Mercedes-Benz", logo: "/brands/cars/mercedes.png" },
  { name: "BMW", logo: "/brands/cars/bmw.png" },
  { name: "Volkswagen", logo: "/brands/cars/volkswagen.png" },
  { name: "Audi", logo: "/brands/cars/audi.png" },
  { name: "Tesla", logo: "/brands/cars/tesla.png" },
];

export const About = () => {
  return (
    <section data-testid="about-section" id="sobre" className="relative pt-28 pb-16 md:pt-36 md:pb-20 bg-[#163A6D]">
      <div className="w-full px-6 md:px-12 lg:px-20 max-w-7xl mx-auto">
        {/* Intro */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div data-testid="about-image-block" className="relative lg:col-span-5 order-2 lg:order-1">
            <div className="relative overflow-hidden rounded-2xl border border-[#2F568F] aspect-[4/3]">
              <img src={ABOUT_IMG} alt="Receção Global Pneus — Hankook Masters certificada" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0E2452]/90 via-transparent to-transparent pointer-events-none" />
              <div className="absolute top-4 left-4 bg-[#0E2452]/90 backdrop-blur-sm border border-[#FF5A00] rounded-full px-3 py-1.5">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#FF5A00] font-display font-bold">Hankook Masters · Oficina certificada</span>
              </div>
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
                <div className="text-white/80 text-xs font-body">A olhar pelo seu amigo de quatro rodas</div>
                <div className="bg-[#FF5A00] rounded-xl px-4 py-2 font-display font-bold text-black text-2xl leading-none">Desde 2003</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <span data-testid="about-label" className="section-label">// Quem somos</span>
            <h2 data-testid="about-title" className="font-display font-bold uppercase text-white text-3xl md:text-4xl leading-[0.98] tracking-tight mt-3 mb-4">
              Mais que uma <span className="text-[#FF5A00]">casa de pneus.</span>
              <br />Uma equipa que se preocupa.
            </h2>
            <p className="text-[#E0E0E0] font-body text-sm md:text-base leading-relaxed mb-6 max-w-2xl">
              A <span className="text-white font-semibold">Global Pneus</span> nasceu na Póvoa de Santa Iria com uma ideia simples: tratar cada carro como se fosse nosso.
              Desde 2003 somos referência na região pela qualidade dos serviços, pela transparência e pela confiança das centenas de clientes que nos recomendam.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 mb-6">
              {VALUES.map((v, i) => (
                <li key={v} data-testid={`about-value-${i}`} className="flex items-start gap-2.5">
                  <span className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center bg-[#FF5A00] flex-shrink-0">
                    <Check className="w-3 h-3 text-black" strokeWidth={3} />
                  </span>
                  <span className="text-[#E0E0E0] font-body text-sm">{v}</span>
                </li>
              ))}
            </ul>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="about-stats">
              {STATS.map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className={`rounded-2xl border p-3.5 ${s.accent ? "bg-[#FF5A00] border-[#FF5A00]" : "bg-[#0E2452]/60 border-[#2F568F]"}`}>
                    <Icon className={`w-4 h-4 mb-2 ${s.accent ? "text-black" : "text-[#FF5A00]"}`} />
                    <div className={`font-display font-bold text-lg leading-none ${s.accent ? "text-black" : "text-white"}`}>{s.value}</div>
                    <div className={`text-[10px] uppercase tracking-widest mt-1 font-display ${s.accent ? "text-black/70" : "text-white/50"}`}>{s.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Location + contact */}
        <div data-testid="about-location-block" className="mt-14 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-7 relative rounded-2xl border border-[#2F568F] overflow-hidden min-h-[300px]">
            <iframe
              title="Localização Global Pneus"
              src="https://www.google.com/maps?q=Praceta+Jaime+Cortes%C3%A3o+6+P%C3%B3voa+de+Santa+Iria&output=embed"
              className="absolute inset-0 w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <a
              data-testid="about-map-link"
              href={COMPANY.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute top-4 left-4 bg-[#0E2452] border border-[#5FA8FF] rounded-full px-4 py-2 flex items-center gap-2 hover:bg-[#5FA8FF] transition-colors group shadow-lg"
            >
              <MapPin className="w-4 h-4 text-[#5FA8FF] group-hover:text-white transition-colors" />
              <span className="text-xs font-display uppercase tracking-widest text-white">Abrir no mapa</span>
            </a>
          </div>
          <div className="lg:col-span-5 rounded-2xl border border-[#2F568F] bg-[#0E2452]/60 p-6 flex flex-col gap-5">
            <div>
              <span className="section-label">// Onde estamos</span>
              <h3 className="font-display font-bold uppercase text-white text-2xl mt-2 leading-tight">
                Visite-nos em <span className="text-[#5FA8FF]">{COMPANY.city}</span>
              </h3>
            </div>
            <div className="space-y-3 text-sm font-body">
              <div className="flex items-start gap-3 text-[#E0E0E0]">
                <MapPin className="w-4 h-4 text-[#FF5A00] mt-0.5 shrink-0" />
                <span>{COMPANY.address}</span>
              </div>
              <a href={COMPANY.landlineHref} className="flex items-center gap-3 text-[#E0E0E0] hover:text-white transition-colors" data-testid="about-phone-link">
                <Phone className="w-4 h-4 text-[#FF5A00] shrink-0" />
                <span>{COMPANY.landline}</span>
              </a>
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 text-[#E0E0E0] hover:text-white transition-colors">
                <Mail className="w-4 h-4 text-[#FF5A00] shrink-0" />
                <span>{COMPANY.email}</span>
              </a>
            </div>
            <div className="border-t border-[#2F568F]/60 pt-4">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/50 font-display mb-2">
                <Clock className="w-3.5 h-3.5" /> Horário
              </div>
              <div className="space-y-1.5">
                {COMPANY.hours.map((h) => (
                  <div key={h.day} className="flex items-center justify-between gap-3 text-sm font-body">
                    <span className="text-white/70">{h.day}</span>
                    <span className={`text-right ${h.time === "Encerrado" ? "text-white/40" : "text-white"}`}>{h.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <ShopGallery />

        {/* Brands */}
        <div className="mt-14 md:mt-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-5">
            <div>
              <span className="section-label">// Parceiros</span>
              <h3 className="font-display font-bold uppercase text-white text-2xl md:text-3xl mt-2">
                Marcas que <span className="text-[#FF5A00]">trabalhamos</span>
              </h3>
            </div>
            <p className="text-white/50 text-xs font-body max-w-sm">Premium, intermédias e económicas — sempre com reputação comprovada.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div data-testid="main-brand-hankook" className="rounded-2xl border border-[#FF5A00] bg-[#FF5A00]/5 p-5 flex flex-col justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF5A00] font-display font-semibold border-r border-[#FF5A00]/40 pr-4 shrink-0 leading-tight">
                  Marca<br />principal
                </div>
                <a
                  href={BRANDS.find((b) => b.name === "Hankook")?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="brand-link-hankook"
                  className="flex-1 min-w-0 hover:opacity-80 transition-opacity"
                >
                  <img src="/logo-hankook.png" alt="Hankook" className="max-h-9 object-contain object-left" />
                </a>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[9px] uppercase tracking-[0.25em] text-[#FF5A00]/80 font-display font-semibold">Equipa de fábrica</span>
                <div className="flex items-center gap-2">
                  {CAR_BRANDS.map((car) => (
                    <div key={car.name} title={car.name} className="h-5 w-7 flex items-center justify-center">
                      <img src={car.logo} alt={car.name} className="max-h-full max-w-full object-contain opacity-90" />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div data-testid="partner-network-card" className="rounded-2xl bg-gradient-to-br from-[#5FA8FF]/10 to-transparent border border-[#5FA8FF]/40 p-5 flex items-center gap-4">
              <img src={COMPANY.partnerLogo} alt="Rede ConfortAuto" className="h-14 w-14 object-contain shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#5FA8FF] font-display font-semibold mb-0.5">Membro oficial da rede</div>
                <div className="font-display font-bold uppercase text-white text-xl tracking-wider leading-none mb-1.5">{COMPANY.partnerName}</div>
                <p className="text-xs text-[#A1A1AA] font-body leading-relaxed">Uma das maiores redes de oficinas ibéricas — padrões de qualidade, garantia alargada e know-how europeu.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ShopGallery = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start", slidesToScroll: 1 });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    const id = setInterval(() => emblaApi.scrollNext(), 4500);
    return () => {
      clearInterval(id);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <div data-testid="shop-gallery" className="mt-14 md:mt-16">
      <div className="flex items-end justify-between gap-4 mb-5">
        <div>
          <span className="section-label">// A nossa casa</span>
          <h3 className="font-display font-bold uppercase text-white text-2xl md:text-3xl mt-2">
            Conheça a oficina <span className="text-[#FF5A00]">por dentro</span>
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="gallery-prev-btn" onClick={() => emblaApi && emblaApi.scrollPrev()} aria-label="Anterior" className="w-9 h-9 rounded-full flex items-center justify-center border border-[#2F568F] text-white hover:bg-[#FF5A00] hover:border-[#FF5A00] transition-colors">
            <ChevronLeft size={16} />
          </button>
          <button data-testid="gallery-next-btn" onClick={() => emblaApi && emblaApi.scrollNext()} aria-label="Seguinte" className="w-9 h-9 rounded-full flex items-center justify-center border border-[#2F568F] text-white hover:bg-[#FF5A00] hover:border-[#FF5A00] transition-colors">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-3">
          {SHOP_PHOTOS.map((photo, idx) => (
            <div key={photo.src} data-testid={`gallery-slide-${idx}`} className="flex-[0_0_80%] sm:flex-[0_0_48%] lg:flex-[0_0_32%] min-w-0 relative group">
              <div className="relative overflow-hidden rounded-2xl bg-[#163A6D] aspect-[16/10] border border-[#2F568F]/60">
                <img src={photo.src} alt={photo.caption} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0E2452]/90 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3.5">
                  <span className="text-white font-display text-xs uppercase tracking-wider font-semibold">{photo.caption}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center gap-1.5 mt-4">
        {SHOP_PHOTOS.map((photo, i) => (
          <button key={photo.src} data-testid={`gallery-dot-${i}`} onClick={() => emblaApi && emblaApi.scrollTo(i)} aria-label={`Foto ${i + 1}`} className={`h-1 rounded-full transition-all duration-300 ${i === selectedIndex ? "w-8 bg-[#FF5A00]" : "w-3 bg-white/30 hover:bg-white/60"}`} />
        ))}
      </div>
    </div>
  );
};

export default About;
